import { useState, useEffect, useCallback } from 'react';
import {
  Users, TrendingUp, CheckCircle, Award, Target, Zap, PhoneCall, Clock,
  GraduationCap, BarChart3, FileText, XCircle, CreditCard, BookOpen,
  Activity, Calendar, AlertCircle
} from 'lucide-react';
import {
  ResponsiveContainer, PieChart as RPieChart, Pie, Cell, Tooltip, Legend,
  AreaChart, Area, XAxis, YAxis, CartesianGrid
} from 'recharts';
import { useTheme } from '../../context/ThemeContext';
import { dashboardService, followUpService } from '../../services/api';
import { KPICard, Skeleton, EmptyState, Badge } from '../../components/ui';
import { STATUS_COLORS, STATUS_NAMES, MONTH_ABBR } from '../../constants';
import DashboardDateFilter from './DashboardDateFilter';

const CURRENT_YEAR  = new Date().getFullYear();
const CURRENT_MONTH = new Date().getMonth();

const defaultParams = () => {
  const start = new Date(CURRENT_YEAR, CURRENT_MONTH, 1);
  const end   = new Date(CURRENT_YEAR, CURRENT_MONTH + 1, 0, 23, 59, 59);
  return {
    startDate: start.toISOString().split('T')[0],
    endDate:   end.toISOString().split('T')[0],
  };
};

const STATUS_ICONS = {
  NEW_LEAD:               Users,
  CONNECTED:              PhoneCall,
  INTERESTED:             TrendingUp,
  FOLLOW_UP_REQUIRED:     Clock,
  APPLICATION_SUBMITTED:  FileText,
  REGISTRATION_COMPLETED: Award,
  PAYMENT_PENDING:        CreditCard,
  ADMISSION_CONFIRMED:    CheckCircle,
  ADMISSION_COMPLETED:    GraduationCap,
  JOINED_BATCH:           BookOpen,
  LOST_LEAD:              XCircle,
};

const fmtTrend = (value) => {
  if (value === undefined || value === null || Number.isNaN(value)) return {};
  return { trend: `${value >= 0 ? '+' : ''}${value}%`, trendUp: value >= 0 };
};

const monthlyTrendToChartData = (monthlyTrend) =>
  (monthlyTrend || []).slice().reverse().map(m => ({
    month:      `${MONTH_ABBR[m._id.month - 1]}`,
    count:      m.count,
    admissions: m.admissions || 0,
  }));

const CounsellorDashboard = () => {
  const { dark } = useTheme();
  const [data, setData]             = useState(null);
  const [todayFollowUps, setTodayFollowUps] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [params, setParams]         = useState(defaultParams);

  const fetchData = useCallback((p) => {
    setLoading(true);
    setData(null);
    Promise.all([
      dashboardService.getCounsellor(p),
      followUpService.getToday(),
    ])
      .then(([dashRes, fuRes]) => {
        setData(dashRes.data.data);
        setTodayFollowUps(Array.isArray(fuRes.data.data) ? fuRes.data.data : []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchData(params); }, [fetchData, params]);

  const cs = { background: dark ? '#1E293B' : '#fff', borderRadius: 14, border: `1px solid ${dark ? '#334155' : '#E2E8F0'}`, overflow: 'hidden' };
  const tt = { background: dark ? '#1E293B' : '#fff', border: `1px solid ${dark ? '#334155' : '#E2E8F0'}`, borderRadius: 8, fontSize: 12 };

  const kpis   = data?.kpis  || {};
  const trends = kpis.trends || {};

  const statusData = (data?.charts?.statusDistribution || [])
    .map(s => ({
      code:  s._id,
      name:  STATUS_NAMES[s._id]  || s._id,
      value: s.count,
      color: STATUS_COLORS[s._id] || '#6B7280',
    }))
    .sort((a, b) => b.value - a.value);

  const monthlyData    = monthlyTrendToChartData(data?.charts?.monthlyPerformance);
  const recentActivity = data?.recentActivity || [];

  return (
    <div>
      {/* Header + filter */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 24 }}>
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: dark ? '#F1F5F9' : '#0F172A', marginBottom: 4 }}>My Dashboard</h2>
          <p style={{ fontSize: 13, color: dark ? '#64748B' : '#94A3B8', margin: 0 }}>Your personal performance and follow-ups</p>
        </div>
        <DashboardDateFilter onChange={p => setParams(p)} />
      </div>

      {loading ? (
        <Skeleton rows={8} cols={4} />
      ) : !data ? (
        <EmptyState title="Dashboard failed to load" />
      ) : (
        <>
          {/* ── KPI cards ─────────────────────────────────────────────────── */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(200px,1fr))', gap: 14, marginBottom: 28 }}>

            {/* Fixed cards — always visible */}
            <KPICard icon={Users}  label="Total Leads"     value={kpis.totalLeads  || 0}          color="#3B82F6" {...fmtTrend(trends.totalLeads)} />
            <KPICard icon={Zap}    label="Today's Leads"   value={kpis.todayLeads  || 0}          color="#8B5CF6" />
            <KPICard icon={Target} label="Conversion Rate" value={`${kpis.conversionRate || 0}%`} color="#22C55E" />

            {/* Dynamic cards — one per lead status present in the selected period */}
            {statusData.map(s => {
              const Icon = STATUS_ICONS[s.code] || Activity;
              return (
                <KPICard
                  key={s.code}
                  icon={Icon}
                  label={s.name}
                  value={s.value}
                  color={s.color}
                />
              );
            })}
          </div>

          {/* ── Follow-up summary ─────────────────────────────────────────── */}
          {/* <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14, marginBottom: 28 }}>
            <KPICard icon={Calendar}     label="Today's Follow-ups"  value={kpis.todayFollowUps    || 0} color="#F59E0B" />
            <KPICard icon={Clock}        label="Upcoming Follow-ups" value={kpis.upcomingFollowUps || 0} color="#3B82F6" />
            <KPICard icon={AlertCircle}  label="Missed Follow-ups"   value={kpis.missedFollowUps   || 0} color="#EF4444" />
          </div> */}

          {/* ── Charts ────────────────────────────────────────────────────── */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>
            <div style={{ background: dark ? '#1E293B' : '#fff', borderRadius: 14, padding: 20, border: `1px solid ${dark ? '#334155' : '#E2E8F0'}` }}>
              <h4 style={{ margin: '0 0 16px', fontSize: 14, fontWeight: 600, color: dark ? '#F1F5F9' : '#0F172A' }}>My Status Distribution</h4>
              {statusData.length === 0 ? <EmptyState icon={BarChart3} title="No leads in this period" /> : (
                <ResponsiveContainer width="100%" height={230}>
                  <RPieChart>
                    <Pie data={statusData} cx="50%" cy="50%" innerRadius={55} outerRadius={95} paddingAngle={2} dataKey="value" nameKey="name">
                      {statusData.map((s, i) => <Cell key={i} fill={s.color} />)}
                    </Pie>
                    <Tooltip contentStyle={tt} />
                    <Legend />
                  </RPieChart>
                </ResponsiveContainer>
              )}
            </div>

            <div style={{ background: dark ? '#1E293B' : '#fff', borderRadius: 14, padding: 20, border: `1px solid ${dark ? '#334155' : '#E2E8F0'}` }}>
              <h4 style={{ margin: '0 0 16px', fontSize: 14, fontWeight: 600, color: dark ? '#F1F5F9' : '#0F172A' }}>My Monthly Performance</h4>
              {monthlyData.length === 0 ? <EmptyState icon={TrendingUp} title="No monthly data" /> : (
                <ResponsiveContainer width="100%" height={230}>
                  <AreaChart data={monthlyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke={dark ? '#334155' : '#E2E8F0'} />
                    <XAxis dataKey="month" tick={{ fontSize: 11, fill: dark ? '#94A3B8' : '#64748B' }} />
                    <YAxis tick={{ fontSize: 11, fill: dark ? '#94A3B8' : '#64748B' }} allowDecimals={false} />
                    <Tooltip contentStyle={tt} />
                    <Legend />
                    <Area type="monotone" dataKey="count"      stroke="#3B82F6" fill="#3B82F6" fillOpacity={0.1} name="Leads" />
                    <Area type="monotone" dataKey="admissions" stroke="#10B981" fill="#10B981" fillOpacity={0.1} name="Admissions" />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* ── Today's follow-ups + Recent activity ──────────────────────── */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            <div style={cs}>
              <h4 style={{ margin: 0, padding: '16px 20px', fontSize: 14, fontWeight: 600, color: dark ? '#F1F5F9' : '#0F172A', borderBottom: `1px solid ${dark ? '#334155' : '#E2E8F0'}` }}>
                Today's Follow-ups
              </h4>
              {todayFollowUps.length === 0 ? (
                <div style={{ padding: 20 }}><EmptyState icon={Calendar} title="No follow-ups scheduled today" /></div>
              ) : (
                todayFollowUps.slice(0, 5).map(f => (
                  <div key={f._id} style={{ padding: '12px 20px', borderBottom: `1px solid ${dark ? '#1E293B' : '#F1F5F9'}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: dark ? '#F1F5F9' : '#0F172A' }}>{f.lead?.fullName}</div>
                        <div style={{ fontSize: 11, color: dark ? '#64748B' : '#94A3B8' }}>
                          {f.scheduledAt ? new Date(f.scheduledAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—'}
                        </div>
                      </div>
                      <Badge color={f.status === 'completed' ? '#10B981' : f.status === 'missed' ? '#EF4444' : '#F59E0B'}>
                        {f.status}
                      </Badge>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div style={cs}>
              <h4 style={{ margin: 0, padding: '16px 20px', fontSize: 14, fontWeight: 600, color: dark ? '#F1F5F9' : '#0F172A', borderBottom: `1px solid ${dark ? '#334155' : '#E2E8F0'}` }}>
                Recent Activity
              </h4>
              {recentActivity.length === 0 ? (
                <div style={{ padding: 20 }}><EmptyState icon={AlertCircle} title="No recent activity" /></div>
              ) : (
                recentActivity.slice(0, 5).map(a => (
                  <div key={a._id} style={{ padding: '10px 20px', borderBottom: `1px solid ${dark ? '#1E293B' : '#F1F5F9'}`, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                    <div style={{ width: 8, height: 8, borderRadius: 4, background: '#3B82F6', marginTop: 5, flexShrink: 0 }} />
                    <div>
                      <div style={{ fontSize: 13, color: dark ? '#F1F5F9' : '#0F172A' }}>{a.description}</div>
                      <div style={{ fontSize: 11, color: dark ? '#64748B' : '#94A3B8' }}>{new Date(a.createdAt).toLocaleString()}</div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CounsellorDashboard;
